import React, { useState } from "react";
import { Container, CustomLink, Filters } from "../../components";
import { sortOptions, validatorCardIcons } from "../../utils/constants";
import ValidatorCard from "../../components/cards/ValidatorCard";
import { useStakingContext } from "../../context/staking-context";
import { filterSearchAndSort, getValidatorImg } from "../../utils/helper";
import ValidatorDataTable from "../../components/dataTable/ValidatorDataTable";
import ClipLoaderComponent from "../../components/loader/ClipLoaderComponent";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import ValidatorEnterPriceCard from "../../components/cards/ValidatorEnterPriceCard";

function ValidatorSection() {
  const navigate = useNavigate();
  const pathName = window?.location?.pathname;
  const {
    validators,
    setSelectedValidator,
    view,
    setView,
    enterpriseValidators,
  } = useStakingContext();
  const [selectedTab, setSelectedTab] = useState("active");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSortOption, setSelectedSortOption] = useState("Stake");
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const totalCardToShow = isMobile ? 4 : 8;
  const filteredAndSortedValidators = filterSearchAndSort(validators, {
    statusKey: "",
    selectedStatus: "",
    searchKey: "name",
    searchTerm: searchQuery,
    sortKey: sortOptions[selectedSortOption?.toLowerCase()],
    sortOrder: "desc",
  });

  const rowsPerPage = pathName !== "/all-validator" ? totalCardToShow : 12; // Number of records per page
  const [page, setPage] = useState(0); // Current page index

  const handlePageChange = (newPage) => {
    setPage(newPage); // Update the current page
  };

  const endRecord = Math.min(
    filteredAndSortedValidators?.length,
    (page + 1) * rowsPerPage
  );
  const startRecord =
    page * rowsPerPage > endRecord ? 1 : page * rowsPerPage + 1;

  return (
    <>
      <section className="relative flex pt-[44px] pb-10">
        <Container>
          <div className="flex items-center gap-3 flex-wrap laptop:flex-nowrap mb-[30px]">
            <div className="flex lg:items-center gap-[6px] lg:gap-3">
              <h2 className="sub-heading font-semibold -tracking-16 text-textcolor">
                Enterprise Validators
              </h2>
            </div>
          </div>
          {enterpriseValidators === undefined && (
            <ClipLoaderComponent
              size={70}
              customClass={"min-h-[130px] justify-center"}
            />
          )}
          {enterpriseValidators?.length === 0 && (
            <div className="min-h-[453px] flex items-center justify-center">
              <h2 className="text-[24px] leading-6 md:text-[30px] md:leading-[30px] font-semibold -tracking-16 text-textcolor">
                No Validators Found
              </h2>
            </div>
          )}
          {enterpriseValidators?.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 laptop:grid-cols-4 gap-3">
              {enterpriseValidators
                ?.slice(0, totalCardToShow)
                ?.map((validator) => (
                  <div className="col-span-1" key={validator?.id}>
                    <ValidatorEnterPriceCard
                      icon={getValidatorImg(validator?.name)}
                      title={validator?.name}
                      tagText={validator?.status}
                      isDisabled={validator?.status === "active" ? false : true}
                    />
                  </div>
                ))}
            </div>
          )}
        </Container>
      </section>
      <section className="relative flex pt-[44px] pb-10">
        <Container>
          <div className="flex items-center gap-3 flex-wrap laptop:flex-nowrap mb-[16px]">
            <h2 className="sub-heading mb-0 font-semibold -tracking-16 text-textcolor flex lg:items-center gap-[6px] lg:gap-3">
              Staking Validators
              <span className="p-1 rounded-lg bg-[#1A1A1A] flex items-center justify-center text-[13px] lg:text-sm font-normal leading-4 -tracking-03 text-white w-[26px] h-[26px] lg:w-10 lg:h-10">
                {validators
                  ? validators?.filter(
                      (validator) =>
                        validator?.name?.toLowerCase() !== "coming soon"
                    ).length
                  : 0}{" "}
              </span>
            </h2>

            <Filters
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              selectedSortOption={selectedSortOption}
              setSelectedSortOption={setSelectedSortOption}
              setView={setView}
              view={view}
              totalRecords={filteredAndSortedValidators?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              endRecord={endRecord}
              startRecord={startRecord}
            />
          </div>
          <p className="mb-8 text-sm md:text-xl">
            Delegate your tokens to these nodes to earn APY.
          </p>

          {view === "grid" ? (
            <>
              {filteredAndSortedValidators === undefined && (
                <ClipLoaderComponent
                  size={100}
                  customClass={"min-h-[453px] justify-center"}
                />
              )}
              {filteredAndSortedValidators?.length === 0 && (
                <div className="min-h-[453px] flex items-center justify-center">
                  <h2 className="text-[24px] leading-6 md:text-[30px] md:leading-[30px] font-semibold -tracking-16 text-textcolor">
                    No Validators Found
                  </h2>
                </div>
              )}
              {filteredAndSortedValidators?.length > 0 && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 laptop:grid-cols-4 gap-3">
                  {filteredAndSortedValidators
                    ?.slice(0, totalCardToShow)
                    ?.map((validator) => (
                      <div className="col-span-1" key={validator?.id}>
                        <ValidatorCard
                          validator={validator}
                          icon={getValidatorImg(validator?.name)}
                          title={validator?.name}
                          tagText={validator?.status}
                          arrowIcon={validatorCardIcons.arrow}
                          commission="Commission"
                          commissionValue={
                            validator?.commission === 0
                              ? "TBD"
                              : validator?.commission * 100 + "%"
                          }
                          checkPoint="Checkpoints signed"
                          checkPointValue={
                            validator?.checkpoint_signed * 100 + "%"
                          }
                          apy="apy"
                          apyValue={
                            validator?.apy === 0
                              ? "TBD"
                              : validator?.apy * 100 + "%"
                          }
                          stakedIcon={validatorCardIcons.staked}
                          stakedValue={validator?.total_staked.toLocaleString(
                            "en-US"
                          )}
                          setSelectedValidator={setSelectedValidator}
                          performance={validator?.performance}
                          contractAddress={validator?.contract_address}
                        />
                      </div>
                    ))}
                </div>
              )}
            </>
          ) : (
            <ValidatorDataTable
              filteredAndSortedValidators={filteredAndSortedValidators?.slice(
                0,
                totalCardToShow
              )}
              page={page}
              rowsPerPage={rowsPerPage}
              setSelectedValidator={setSelectedValidator}
              endRecord={endRecord}
              startRecord={startRecord}
              isUndefined={filteredAndSortedValidators === undefined}
            />
          )}
          {pathName !== "/all-validator" && view !== "list" && (
            <CustomLink
              id="seeAll"
              onClick={() => navigate("/all-validator")}
              className="bg-[#1A1A1A] !rounded-full py-2 px-4 hover:text-green h-10 flex md:hidden whitespace-nowrap w-full mt-4"
            >
              See All
              <span className="ml-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="6"
                  height="12"
                  viewBox="0 0 6 12"
                  fill="none"
                >
                  <path
                    d="M1 1L5 6L1 11"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </CustomLink>
          )}
        </Container>
      </section>
    </>
  );
}

export default ValidatorSection;
